/**
 * Editorial map
 * Renders the studio location with Mapbox GL.
 *
 * Mapbox GL is roughly 800KB of JS plus a stylesheet, so it is fetched only
 * when a map actually scrolls into view rather than on every page load. With no
 * token configured nothing is fetched and the section keeps its static plate,
 * so an unset token is a quiet no-op rather than a broken embed.
 *
 * The token is a Mapbox PUBLIC token (pk.*), designed to be visible
 * client-side; it belongs in a theme setting restricted by URL in the Mapbox
 * dashboard. A secret token (sk.*) must never be used here.
 */
(function () {
  'use strict';

  var CDN_JS = 'https://api.mapbox.com/mapbox-gl-js/v3.9.0/mapbox-gl.js';
  var CDN_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.9.0/mapbox-gl.css';
  var loading = null;

  /**
   * Default pin
   * The numbered "31" marker exported from the design (the studio house
   * number at Widenmayerstrasse 31). Used whenever no pin image is uploaded
   * in the section settings, so the drawn marker ships without an upload.
   */
  var PIN_SVG =
    '<svg width="46" height="61" viewBox="0 0 46 61" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<g filter="url(#wearr-pin-shadow)">' +
    '<path d="M23 0.25C33.3553 0.25 41.75 8.64466 41.75 19C41.75 21.5804 40.9485 24.9537 39.6396 28.5762C38.3325 32.1938 36.5262 36.0406 34.5322 39.5615C32.5377 43.0835 30.3605 46.2717 28.3135 48.5752C27.2896 49.7273 26.3035 50.6517 25.3955 51.2861C24.4829 51.9237 23.6749 52.25 23 52.25C22.3251 52.25 21.5171 51.9237 20.6045 51.2861C19.6965 50.6517 18.7104 49.7273 17.6865 48.5752C15.6395 46.2717 13.4623 43.0835 11.4678 39.5615C9.47385 36.0406 7.66749 32.1938 6.36035 28.5762C5.05147 24.9537 4.25 21.5804 4.25 19C4.25 8.64466 12.6447 0.25 23 0.25Z" fill="#F54925" stroke="#B02507" stroke-width="0.5"/>' +
    '<circle cx="23" cy="18" r="11" fill="#F4EDE6"/>' +
    '<path d="M19.9535 22.104C19.4162 22.104 18.9222 22 18.4715 21.792C18.0295 21.5753 17.6655 21.2893 17.3795 20.934C17.0935 20.5787 16.9289 20.1757 16.8855 19.725H17.8735C17.9169 19.9937 18.0382 20.2363 18.2375 20.453C18.4369 20.6697 18.6839 20.843 18.9785 20.973C19.2819 21.0943 19.6069 21.155 19.9535 21.155C20.5689 21.155 21.0455 20.999 21.3835 20.687C21.7302 20.375 21.9035 19.998 21.9035 19.556C21.9035 19.2613 21.8212 18.9927 21.6565 18.75C21.5005 18.4987 21.2839 18.2993 21.0065 18.152C20.7292 17.996 20.4085 17.918 20.0445 17.918H19.0565V17.021H19.9275C20.2309 17.021 20.5082 16.9647 20.7595 16.852C21.0195 16.7307 21.2275 16.566 21.3835 16.358C21.5395 16.1413 21.6175 15.8943 21.6175 15.617C21.6175 15.2097 21.4702 14.8803 21.1755 14.629C20.8895 14.369 20.4649 14.239 19.9015 14.239C19.3729 14.239 18.9482 14.3603 18.6275 14.603C18.3155 14.8457 18.1292 15.1317 18.0685 15.461H17.1195C17.1629 15.1057 17.2929 14.7633 17.5095 14.434C17.7349 14.1047 18.0469 13.8317 18.4455 13.615C18.8442 13.3983 19.3209 13.29 19.8755 13.29C20.7249 13.29 21.3879 13.5067 21.8645 13.94C22.3499 14.3647 22.5925 14.9107 22.5925 15.578C22.5925 15.864 22.5232 16.137 22.3845 16.397C22.2545 16.6483 22.0899 16.865 21.8905 17.047C21.6912 17.229 21.4919 17.3503 21.2925 17.411C21.7952 17.6017 22.1852 17.8877 22.4625 18.269C22.7399 18.6503 22.8785 19.0707 22.8785 19.53C22.8785 20.0413 22.7485 20.492 22.4885 20.882C22.2285 21.2633 21.8775 21.5623 21.4355 21.779C20.9935 21.9957 20.4995 22.104 19.9535 22.104ZM27.075 22V14.33H25.255V13.394H28.05V22H27.075Z" fill="#B02507"/>' +
    '</g>' +
    '<defs><filter id="wearr-pin-shadow" x="0" y="0" width="46" height="60.5" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">' +
    '<feFlood flood-opacity="0" result="BackgroundImageFix"/>' +
    '<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>' +
    '<feOffset dy="4"/><feGaussianBlur stdDeviation="2"/><feComposite in2="hardAlpha" operator="out"/>' +
    '<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>' +
    '<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>' +
    '<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>' +
    '</filter></defs></svg>';

  function loadLibrary() {
    if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
    if (loading) return loading;

    loading = new Promise(function (resolve, reject) {
      var css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = CDN_CSS;
      document.head.appendChild(css);

      var script = document.createElement('script');
      script.src = CDN_JS;
      script.async = true;
      script.onload = function () {
        resolve(window.mapboxgl);
      };
      script.onerror = function () {
        reject(new Error('Mapbox GL failed to load'));
      };
      document.head.appendChild(script);
    });
    return loading;
  }

  function build(root) {
    if (root.dataset.mapReady === 'true') return;

    var token = root.getAttribute('data-token');
    var lat = parseFloat(root.getAttribute('data-lat'));
    var lng = parseFloat(root.getAttribute('data-lng'));
    if (!token || !isFinite(lat) || !isFinite(lng)) return;

    root.dataset.mapReady = 'true';

    loadLibrary()
      .then(function (mapboxgl) {
        mapboxgl.accessToken = token;

        var canvas = document.createElement('div');
        canvas.className = 'editorial-map__canvas';
        root.appendChild(canvas);

        var map = new mapboxgl.Map({
          container: canvas,
          style: root.getAttribute('data-style') || 'mapbox://styles/mapbox/light-v11',
          center: [lng, lat],
          zoom: parseFloat(root.getAttribute('data-zoom')) || 14,
          attributionControl: true,
          /* The map sits mid-article, so wheel zoom would swallow page scroll.
             Ctrl/cmd + wheel still zooms, and drag still pans. */
          scrollZoom: false
        });

        map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

        var pinUrl = root.getAttribute('data-pin');
        var element = document.createElement('div');
        element.className = 'editorial-map__pin';
        if (pinUrl) {
          var img = document.createElement('img');
          img.src = pinUrl;
          img.alt = '';
          element.appendChild(img);
        } else {
          element.innerHTML = PIN_SVG;
        }

        new mapboxgl.Marker({ element: element, anchor: 'bottom' })
          .setLngLat([lng, lat])
          .addTo(map);

        root.classList.add('editorial-map--live');

        /* Sections are re-rendered wholesale by the theme editor, so drop the
           GL context when this instance goes away rather than leaking it. */
        document.addEventListener('shopify:section:unload', function (event) {
          if (event.target.contains(root)) map.remove();
        });
      })
      .catch(function () {
        /* Leave the static plate in place; nothing to announce to the shopper. */
        root.dataset.mapReady = 'false';
      });
  }

  function observe(root) {
    if (!('IntersectionObserver' in window)) {
      build(root);
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.disconnect();
          build(root);
        });
      },
      { rootMargin: '200px' }
    );
    io.observe(root);
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-editorial-map]').forEach(observe);
  }

  document.addEventListener('DOMContentLoaded', function () {
    init();
  });
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  if (document.readyState !== 'loading') init();
})();
